import React, { useRef } from 'react';
/**
 * DjayAvatar — render primitive (orchestrator).
 *
 * Split from the former ~1619-line `DjayAvatar.tsx` god-file into a scalable
 * `mascot/avatar/` module (MASCOT_SSOT_ARCHITECTURE_AUDIT_2026 §4.1). This
 * orchestrator owns ONLY: size→detail-flag derivation, the unique gradient
 * `uid`, the outer <svg> shell + body gradient <defs>, and the assembly of the
 * body layers + the always-rendered floating accessory. All per-feature SVG
 * lives in `./parts/*`.
 *
 * This stays the canonical render primitive: product code must use the intent
 * wrappers `<DjayIdentity>` / `<DjaySpecimen>` / `<DjayNarrator>` instead of
 * rendering `<DjayAvatar>` directly (lint-guarded, see .eslintrc.json §6/§9.6).
 *
 * Import note (audit ronde 10): only `useRef` is needed from React — JSX is
 * transformed by the bundler. Adding unused React imports has regressed twice.
 */
import { SIZES, DJAY_VIEWBOX, DETAIL_THRESHOLD, ARM_STUB_MIN, MOUTH_MIN } from './types';
import type { DjayAvatarProps } from './types';
import { VARIANT_COLORS } from './palette';
import { ensureAnimationStyles } from './animationStyles';
import { BodyLayers } from './parts/BodyLayers';
import { Accessory } from './parts/Accessory';
import { Treatment } from './parts/Treatment';
export function DjayAvatar({
  size = 'md',
  emotion = 'happy',
  tier = 'rookie',
  variant = 'default',
  accessory = 'none',
  treatment = 'none',
  className = '',
  animate = true,
  hideBody = false
}: DjayAvatarProps) {
  const pixelSize = SIZES[size];
  const showDetails = pixelSize >= DETAIL_THRESHOLD;
  const showArmStubs = pixelSize >= ARM_STUB_MIN && pixelSize < DETAIL_THRESHOLD;
  const showFullArms = pixelSize >= DETAIL_THRESHOLD;
  const showTail = pixelSize >= DETAIL_THRESHOLD;
  const showMouth = pixelSize >= MOUTH_MIN;
  // If hideBody is true, force showAccessory regardless of size so we can
  // render just the accessory.
  const showAccessory = (pixelSize >= DETAIL_THRESHOLD || hideBody) && accessory !== 'none';
  const uidRef = useRef(`djay-${size}-${variant}-${Math.random().toString(36).slice(2, 6)}`);
  const uid = uidRef.current;
  const colors = VARIANT_COLORS[variant] ?? VARIANT_COLORS.default;
  const hasTreatment = treatment !== 'none' && !hideBody;
  // Glowing eyes accompany the lit finishes.
  const eyeGlow = treatment === 'glow' || treatment === 'gummy' || treatment === 'aura';
  // Inject animation styles once (singleton)
  if (animate) ensureAnimationStyles();
  return <div className={`relative inline-flex items-center justify-center ${className}`} data-id="element-3011">
      <svg width={pixelSize} height={pixelSize} viewBox={DJAY_VIEWBOX} fill="none" xmlns="http://www.w3.org/2000/svg" className={`djay-avatar ${animate ? 'djay-animated' : ''}`} style={{
      overflow: 'visible'
    }} role="img" aria-label={`Djay mascotte (${emotion})`} data-id="element-3012">
        <defs data-id="element-3013">
          {variant === 'rainbow' ? <linearGradient id={`${uid}-body-grad`} x1="35" y1="45" x2="165" y2="195" gradientUnits="userSpaceOnUse" data-id="element-3014">
              <stop offset="0%" stopColor="#F87171" data-id="element-3015" />
              <stop offset="20%" stopColor="#FB923C" data-id="element-3016" />
              <stop offset="40%" stopColor="#FBBF24" data-id="element-3017" />
              <stop offset="60%" stopColor="#34D399" data-id="element-3018" />
              <stop offset="80%" stopColor="#60A5FA" data-id="element-3019" />
              <stop offset="100%" stopColor="#A78BFA" data-id="element-3020" />
            </linearGradient> : <linearGradient id={`${uid}-body-grad`} x1="35" y1="45" x2="165" y2="195" gradientUnits="userSpaceOnUse" data-id="element-3021">
              <stop offset="0%" stopColor={colors.bodyGradient[0]} data-id="element-3022" />
              <stop offset="45%" stopColor={colors.bodyGradient[1]} data-id="element-3023" />
              <stop offset="100%" stopColor={colors.bodyGradient[2]} data-id="element-3024" />
            </linearGradient>}
          {/* Soft top-left sheen — gives the body a rounded, lit-from-above
           read. Palette-agnostic (white→transparent), so it layers
           correctly over every variant without a per-variant entry. */}
          <radialGradient id={`${uid}-body-sheen`} cx="38%" cy="32%" r="60%" gradientUnits="objectBoundingBox" data-id="element-3025">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.55" data-id="element-3026" />
            <stop offset="45%" stopColor="#FFFFFF" stopOpacity="0.12" data-id="element-3027" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" data-id="element-3028" />
          </radialGradient>
          {/* Bottom inner shade — deepens the lower belly so the form has
           volume rather than reading flat. Uses the darkest gradient stop. */}
          <radialGradient id={`${uid}-body-shade`} cx="55%" cy="92%" r="62%" gradientUnits="objectBoundingBox" data-id="element-3029">
            <stop offset="0%" stopColor={colors.bodyGradient[2]} stopOpacity="0.55" data-id="element-3030" />
            <stop offset="60%" stopColor={colors.bodyGradient[2]} stopOpacity="0.14" data-id="element-3031" />
            <stop offset="100%" stopColor={colors.bodyGradient[2]} stopOpacity="0" data-id="element-3032" />
          </radialGradient>
          {hasTreatment && <>
              <radialGradient id={`${uid}-treatment-radial`} cx="50%" cy="55%" r="55%" data-id="element-3033">
                <stop offset="0%" stopColor={colors.treatmentAccent} stopOpacity="0.65" data-id="element-3034" />
                <stop offset="55%" stopColor={colors.treatmentAccent} stopOpacity="0.22" data-id="element-3035" />
                <stop offset="100%" stopColor={colors.treatmentAccent} stopOpacity="0" data-id="element-3036" />
              </radialGradient>
              <filter id={`${uid}-treatment-soft`} x="-30%" y="-30%" width="160%" height="160%" data-id="element-3037">
                <feGaussianBlur stdDeviation="4" data-id="element-3038" />
              </filter>
            </>}
        </defs>
        {hasTreatment && <g className="djay-treatment-under" data-id="element-3039">
            <Treatment treatment={treatment} layer="under" uid={uid} colors={colors} showDetails={showDetails} data-id="element-3040" />
          </g>}
        {!hideBody && <BodyLayers uid={uid} colors={colors} emotion={emotion} tier={tier} variant={variant} showTail={showTail} showDetails={showDetails} showMouth={showMouth} showFullArms={showFullArms} showArmStubs={showArmStubs} eyeGlow={eyeGlow} data-id="element-3041" />}
        {hasTreatment && <g className="djay-treatment-over" data-id="element-3042">
            <Treatment treatment={treatment} layer="over" uid={uid} colors={colors} showDetails={showDetails} data-id="element-3043" />
          </g>}
        <g className="djay-accessory-float" data-id="element-3044">
          <Accessory accessory={accessory} uid={uid} showAccessory={showAccessory} data-id="element-3045" />
        </g>
      </svg>
    </div>;
}